// src/workspace/PaperAnalysisHub.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Research Paper Analysis — End-to-end workflow for understanding any paper.
// Upload → Structure → Concepts → Methodology → Results → Citations →
// Connections → Reproduce → Critique → Report
//
// Design principle: structural intelligence via pure computation.
// AI agents augment but the pipeline itself cannot be replaced by prompts.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  Treemap,
} from 'recharts';
import { C, PALETTE } from './labs/wsShared';
import { addWorkspaceItem, getAuthUserKey, readWorkspace } from './data/workspaceStore';
import { startUsageSession, usageStep, usageWorkflowFromTabs } from '../utils/usageTracker';
import {
  extractPdfText, readFileAsText, detectSections, extractMetadata,
  extractConcepts, extractCitations, detectMethodology, detectClaims,
  analyzeReadability, scoreReproducibility, analyzePaper,
} from './paperAnalysis/paperEngine';
import { papers } from '../api/client';

// ── Design Tokens ─────────────────────────────────────────────────────────────
const FONT = "'JetBrains Mono', monospace";
const ts = () => new Date().toLocaleTimeString('en-GB', { hour12: false });

// ── Hub Tabs ──────────────────────────────────────────────────────────────────
const HUB_TABS = [
  { id: 'upload',       label: 'UPLOAD',        icon: '📎', color: C.cyan   },
  { id: 'structure',    label: 'STRUCTURE',      icon: '🏗️', color: C.blue   },
  { id: 'concepts',     label: 'CONCEPTS',       icon: '🧬', color: C.violet },
  { id: 'methodology',  label: 'METHODOLOGY',    icon: '⚙️', color: C.orange },
  { id: 'results',      label: 'RESULTS',        icon: '📊', color: C.green  },
  { id: 'citations',    label: 'CITATIONS',      icon: '🔗', color: C.gold   },
  { id: 'connections',  label: 'CONNECTIONS',     icon: '🌐', color: C.pink   },
  { id: 'reproduce',    label: 'REPRODUCE',      icon: '🔬', color: C.cyan   },
  { id: 'critique',     label: 'CRITIQUE',       icon: '🎯', color: C.red    },
  { id: 'report',       label: 'REPORT',         icon: '📄', color: C.green  },
];

const USAGE_WORKFLOW = HUB_TABS.map(t => t.id);

// ── Shared Styles ─────────────────────────────────────────────────────────────
const panelStyle = {
  background: C.deep, border: `1px solid ${C.b1}`, borderRadius: 4, padding: 14, marginBottom: 10,
};
const headStyle = {
  fontSize: 10, fontWeight: 700, letterSpacing: '.1em', color: C.t2,
  textTransform: 'uppercase', marginBottom: 8, fontFamily: FONT,
};
const metricBox = (color) => ({
  background: C.surface, border: `1px solid ${C.b0}`, borderLeft: `3px solid ${color}`,
  borderRadius: 3, padding: '10px 12px', fontFamily: FONT,
});
const smallLabel = { fontSize: 9, color: C.t3, letterSpacing: '.08em', textTransform: 'uppercase', fontFamily: FONT };
const bigVal = (color) => ({ fontSize: 22, fontWeight: 800, color, fontFamily: FONT, letterSpacing: '-.02em' });
const chartTooltip = { contentStyle: { background: C.raised, border: `1px solid ${C.b1}`, borderRadius: 3, fontSize: 11, fontFamily: FONT, color: C.t2 } };
const tagStyle = (color) => ({
  display: 'inline-block', padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 600,
  background: `${color}18`, color, border: `1px solid ${color}30`, fontFamily: FONT, marginRight: 4, marginBottom: 4,
});

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function PaperAnalysisHub({ onBack, isAuthenticated, authUser, workspaceRestore, onWorkspaceRestored }) {
  // ── State ───────────────────────────────────────────────────────────────────
  const [tab, setTab] = useState('upload');
  const [logs, setLogs] = useState([{ ts: ts(), msg: 'Research Paper Analysis ready.', t: 'ok' }]);
  const [file, setFile] = useState(null);
  const [rawText, setRawText] = useState('');
  const [pdfPages, setPdfPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  // Analysis results
  const [analysis, setAnalysis] = useState(null);

  // AI enhancement
  const [aiData, setAiData] = useState(null);
  const [aiRunning, setAiRunning] = useState(false);
  const [aiProgress, setAiProgress] = useState(0);
  const [aiStage, setAiStage] = useState('');
  const [aiUsed, setAiUsed] = useState(0);
  const [paperHash, setPaperHash] = useState('');
  const [aiMsg, setAiMsg] = useState('');
  const [uploadSrc, setUploadSrc] = useState('file');
  const [urlVal, setUrlVal] = useState('');
  const [isDrag, setIsDrag] = useState(false);

  const logRef = useRef(null);
  const dropRef = useRef(null);

  const log = useCallback((msg, t = 'info') => setLogs(l => [...l.slice(-120), { ts: ts(), msg, t }]), []);
  const hasData = !!analysis;

  const isPaid = isAuthenticated && authUser &&
    ['annual', 'lifetime', 'researcher', 'pro'].includes(authUser?.tier);

  const computeHash = useCallback(async (text) => {
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text.slice(0, 50000)));
    return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
  }, []);

  // ── Usage tracking ──────────────────────────────────────────────────────────
  useEffect(() => {
    startUsageSession('paper_analysis');
  }, []);

  const switchTab = useCallback((id) => {
    setTab(id);
    usageStep('paper_analysis', id, null, USAGE_WORKFLOW);
  }, []);

  // ── File handlers ───────────────────────────────────────────────────────────
  const handleFile = useCallback(async (f) => {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['pdf', 'txt', 'md', 'tex'].includes(ext)) {
      log('Unsupported format. Upload PDF, TXT, MD, or TEX.', 'err');
      return;
    }
    setFile(f);
    setLoading(true);
    setProgress(0);
    log(`Loading: ${f.name} (${(f.size / 1024).toFixed(1)} KB)`, 'info');

    try {
      let text = '';
      let pages = [];
      if (ext === 'pdf') {
        log('Extracting PDF text...', 'info');
        const result = await extractPdfText(f, pct => setProgress(pct));
        text = result.text;
        pages = result.pages;
      computeHash(text).then(h => setPaperHash(h));
        setPdfPages(pages);
        log(`Extracted ${result.numPages} pages, ${text.split(/\s+/).length} words`, 'ok');
      } else {
        text = await readFileAsText(f);
        log(`Loaded text file: ${text.split(/\s+/).length} words`, 'ok');
      }

      setRawText(text);
      log('Running structural analysis pipeline...', 'info');

      // Run the full analysis pipeline (all pure computation, no AI calls)
      await new Promise(r => setTimeout(r, 50)); // yield to UI
      const result = analyzePaper(text);
      setAnalysis(result);

      log(`Detected ${result.sections.length} sections, ${result.concepts.concepts.length} key concepts`, 'ok');
      log(`${result.citations.totalRefs} references, ${result.methodology.methods.length} methodologies identified`, 'ok');
      log(`Reproducibility: ${result.reproducibility.score}/100 — ${result.reproducibility.label}`, 'ok');
      log('✓ Full analysis complete. Navigate tabs to explore.', 'ok');

      setTab('structure');
    } catch (e) {
      log(`Error: ${e.message}`, 'err');
    } finally {
      setLoading(false);
    }
  }, [log]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  }, [handleFile]);

  const onDragOver = useCallback((e) => { e.preventDefault(); }, []);

  const fetchURL = useCallback(async (url) => {
    if (!String(url || '').trim()) { log('Enter a URL', 'err'); return; }
    const name = String(url).split('/').pop() || 'paper.pdf';
    try {
      log(`Fetching: ${url}`, 'info');
      const response = await fetch(String(url).trim());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      handleFile(new File([blob], name, { type: blob.type }));
    } catch (e) {
      log(`Fetch failed: ${e.message}`, 'err');
    }
  }, [handleFile, log]);

  // ── Workspace restore ───────────────────────────────────────────────────────
  useEffect(() => {

    if (workspaceRestore && workspaceRestore.module_id === 'paper_analysis') {
      onWorkspaceRestored?.();
    }

  }, [workspaceRestore, onWorkspaceRestored]);

  // ── Sample paper ────────────────────────────────────────────────────────────
  const loadSample = useCallback(() => {
    const sampleText = SAMPLE_ABSTRACT;
    setRawText(sampleText);
    setFile({ name: 'sample-attention-paper.txt', size: sampleText.length });
    setLoading(true);
    log('Loading sample: Attention Is All You Need (excerpt)', 'info');
    computeHash(sampleText).then(h => setPaperHash(h));
    setTimeout(() => {
      const result = analyzePaper(sampleText);
      setAnalysis(result);
      setLoading(false);
      log('✓ Sample analyzed. Explore the tabs.', 'ok');
      setTab('structure');
    }, 100);
  }, [log, computeHash]);

  // ── AI Enhancement ──────────────────────────────────────────────────────────
  const runAI = useCallback(async () => {
    if (!rawText || !paperHash) { log('Upload a paper first.', 'err'); return; }
    if (!isPaid) {
      setAiMsg('🔒 AI Analysis is a Paid tier feature (30 papers/month). Upgrade to unlock.');
      return;
    }
    setAiMsg('');
    setAiRunning(true);
    setAiProgress(5);
    setAiStage('Checking cache');
    try {
      // 1. Check if already cached
      const cached = await papers.checkCache(paperHash);
      setAiProgress(30);
      if (cached?.cached) {
        setAiData(cached.ai_data);
        setAiUsed(cached.monthly_used ?? aiUsed);
        log('✓ Loaded from cache — no API call needed.', 'ok');
        return;
      }
      // 2. Run analysis on backend
      setAiStage('Analyzing');
      setAiProgress(50);
      log(`Running AI analysis (${cached?.monthly_used ?? 0}/30 used this month)…`, 'info');
      const result = await papers.runAI(paperHash, rawText);
      setAiProgress(95);
      setAiData(result.ai_data);
      setAiUsed(result.monthly_used ?? 0);
      log('✓ AI enhancement complete! All tabs now show enriched insights.', 'ok');
    } catch (e) {
      if (e.status === 403) setAiMsg('🔒 AI Analysis is a Paid tier feature. Upgrade to access.');
      else if (e.status === 429) setAiMsg('Monthly limit reached (30/30). Resets next month.');
      else log(`AI Enhancement error: ${e.message}`, 'err');
    } finally {
      setAiRunning(false);
      setAiProgress(0);
      setAiStage('');
    }
  }, [rawText, paperHash, isPaid, aiUsed, log]);

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ fontFamily: FONT, color: C.t1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* ── Tab Bar ──────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 2, padding: '8px 10px 0', flexShrink: 0, overflowX: 'auto', borderBottom: `1px solid ${C.b0}` }}>
        {HUB_TABS.map(t => {
          const active = tab === t.id;
          const disabled = t.id !== 'upload' && !hasData;
          return (
            <button
              key={t.id}
              onClick={() => !disabled && switchTab(t.id)}
              style={{
                background: active ? `${t.color}15` : 'transparent',
                border: `1px solid ${active ? `${t.color}60` : 'transparent'}`,
                borderBottom: active ? `2px solid ${t.color}` : '2px solid transparent',
                color: disabled ? C.t4 : active ? t.color : C.t2,
                padding: '7px 10px 6px', borderRadius: '4px 4px 0 0', cursor: disabled ? 'not-allowed' : 'pointer',
                fontSize: 9.5, fontWeight: 700, letterSpacing: '.08em', fontFamily: FONT,
                opacity: disabled ? 0.4 : 1, transition: 'all .15s', whiteSpace: 'nowrap',
              }}
            >
              {t.icon} {t.label}
            </button>
          );
        })}
      </div>

      {/* ── AI strip (visible once paper loaded) ───────────────────────────── */}
      {hasData && (
        <div style={{ flexShrink: 0, background: C.deep, borderBottom: `1px solid ${C.b0}`, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.08em', color: aiData ? C.green : C.violet, fontFamily: FONT }}>
            {aiData ? '● AI ACTIVE' : '🧠 AI ENHANCEMENT'}
          </span>
          {isPaid && !aiData && !aiRunning && (
            <span style={{ fontSize: 9, color: C.t4 }}>{aiUsed}/30 this month</span>
          )}
          <button
            onClick={runAI}
            disabled={aiRunning}
            style={{ background: !isPaid ? C.surface : aiRunning ? C.surface : `${C.green}15`, border: `1px solid ${!isPaid ? C.b1 : aiRunning ? C.b1 : `${C.green}50`}`, color: !isPaid ? C.t4 : aiRunning ? C.t3 : C.green, padding: '4px 14px', borderRadius: 3, cursor: aiRunning ? 'not-allowed' : 'pointer', fontSize: 9, fontWeight: 700, fontFamily: FONT, letterSpacing: '.06em' }}
          >
            {aiRunning ? `⏳ ${aiStage}…` : aiData ? '🔄 RE-RUN AI' : '▶ RUN AI FULL ANALYSIS'}
          </button>
          {!isPaid && (
            <span style={{ fontSize: 9, color: C.gold }}>🔒 Paid tier only · 30 papers/month</span>
          )}
          {aiMsg && !aiRunning && (
            <span style={{ fontSize: 9, color: C.gold }}>{aiMsg}</span>
          )}
          {aiRunning && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ background: C.b0, borderRadius: 4, height: 5, width: 120, overflow: 'hidden' }}>
                <div style={{ background: C.green, height: '100%', width: `${aiProgress}%`, transition: 'width .3s', borderRadius: 4 }} />
              </div>
              <span style={{ fontSize: 8, color: C.t4 }}>{aiProgress}%</span>
            </div>
          )}
          {aiData && !aiRunning && <span style={{ fontSize: 9, color: C.green }}>✓ AI insights active in all tabs</span>}
        </div>
      )}

      {/* ── Content ──────────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflow: 'auto', padding: tab === 'upload' ? 0 : 12 }}>

        {/* ── UPLOAD TAB ─────────────────────────────────────────────────────── */}
        {tab === 'upload' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 40px 32px', minHeight: '100%', position: 'relative' }}>
            <div style={{ position: 'absolute', top: '10%', left: '50%', transform: 'translateX(-50%)', width: 700, height: 300, background: `radial-gradient(ellipse,${C.violet}08 0%,transparent 70%)`, pointerEvents: 'none' }} />
            <div style={{ textAlign: 'center', marginBottom: 40, position: 'relative' }}>
              <h1 style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: 38, color: C.t1, lineHeight: 1.1, margin: '0 0 12px' }}>
                Read Smarter. Think Deeper.
                <br />
                <span style={{ background: `linear-gradient(90deg,${C.violet},${C.cyan})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Research Paper Analysis
                </span>
              </h1>
              <p style={{ fontSize: 12, color: C.t3, maxWidth: 700, lineHeight: 1.85, margin: '0 auto' }}>
                Structure · Concepts · Methodology · Results · Citations · Reproducibility · Critique
              </p>
              
            </div>

            <div style={{ width: '100%', maxWidth: 720, background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 8, overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,.6)', position: 'relative', marginBottom: 28 }}>
              <div style={{ display: 'flex', background: C.deep, borderBottom: `1px solid ${C.b0}` }}>
                {[['file', '📄 Upload File'], ['url', '🌐 From URL'], ['sample', '📚 Sample Papers']].map(([id, label]) => (
                  <button key={id} onClick={() => setUploadSrc(id)} style={{ flex: 1, padding: '14px', border: 'none', background: uploadSrc === id ? C.surface : 'transparent', color: uploadSrc === id ? C.cyan : C.t3, fontFamily: FONT, fontSize: 9, fontWeight: 700, cursor: 'pointer', borderBottom: `2px solid ${uploadSrc === id ? C.cyan : 'transparent'}`, transition: 'all .12s' }}>{label}</button>
                ))}
              </div>
              <div style={{ padding: 30 }}>
                {uploadSrc === 'file' && (
                  <div
                    onDragOver={(e) => { e.preventDefault(); setIsDrag(true); }}
                    onDragLeave={() => setIsDrag(false)}
                    onDrop={(e) => { e.preventDefault(); setIsDrag(false); handleFile(e.dataTransfer?.files?.[0]); }}
                    onClick={() => { if (!loading) { const inp = document.createElement('input'); inp.type = 'file'; inp.accept = '.pdf,.txt,.md,.tex'; inp.onchange = ev => handleFile(ev.target.files[0]); inp.click(); } }}
                    style={{ border: `2px dashed ${isDrag ? C.cyan : C.b2}`, borderRadius: 8, padding: '60px 20px', textAlign: 'center', cursor: loading ? 'wait' : 'pointer', background: isDrag ? `${C.cyan}07` : C.deep, transition: 'all .2s' }}
                  >
                    <div style={{ fontSize: 52, marginBottom: 12, opacity: 0.5 }}>{loading ? '⏳' : '📄'}</div>
                    <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 700, fontSize: 17, color: C.t1, marginBottom: 6 }}>{loading ? 'Analysing...' : 'Drop your paper here'}</div>
                    <div style={{ fontSize: 11, color: C.t3 }}>{loading ? (progress > 0 ? `${progress}% complete…` : 'Extracting text…') : 'or click to browse · PDF / TXT / MD / TEX'}</div>
                    {loading && progress > 0 && (
                      <div style={{ marginTop: 14, maxWidth: 260, margin: '14px auto 0' }}>
                        <div style={{ background: C.b0, borderRadius: 4, height: 5, overflow: 'hidden' }}>
                          <div style={{ background: C.cyan, height: '100%', width: `${progress}%`, transition: 'width .2s', borderRadius: 4 }} />
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {uploadSrc === 'url' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ fontSize: 9, color: C.t4, fontFamily: FONT }}>PUBLIC PAPER URL (PDF / TXT / MD)</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input value={urlVal} onChange={e => setUrlVal(e.target.value)} placeholder="https://arxiv.org/pdf/1706.03762.pdf" onKeyDown={e => e.key === 'Enter' && fetchURL(urlVal)} style={{ flex: 1, background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: '12px 14px', fontSize: 11, fontFamily: FONT, borderRadius: 4, outline: 'none' }} />
                      <button onClick={() => fetchURL(urlVal)} disabled={loading} style={{ padding: '12px 22px', background: `${C.cyan}18`, border: `1px solid ${C.cyan}`, color: C.cyan, fontFamily: FONT, fontSize: 10, fontWeight: 700, borderRadius: 4, cursor: 'pointer' }}>{loading ? '…' : 'FETCH'}</button>
                    </div>
                  </div>
                )}
                {uploadSrc === 'sample' && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8 }}>
                    {[
                      { name: 'Attention Is All You Need', authors: 'Vaswani et al., 2017', desc: 'Transformer · NLP · Self-attention mechanism' },
                    ].map((s, i) => (
                      <button key={i} onClick={loadSample} disabled={loading} style={{ padding: '16px', background: C.deep, border: `1px solid ${C.b1}`, borderRadius: 6, cursor: 'pointer', textAlign: 'left', transition: 'border-color .15s', opacity: loading ? 0.6 : 1 }} onMouseEnter={e => e.currentTarget.style.borderColor = C.cyan} onMouseLeave={e => e.currentTarget.style.borderColor = C.b1}>
                        <div style={{ fontSize: 20, marginBottom: 6 }}>📖</div>
                        <div style={{ fontFamily: FONT, fontWeight: 700, fontSize: 11, color: C.t1, marginBottom: 3 }}>{s.name}</div>
                        <div style={{ fontSize: 8.5, color: C.t4, lineHeight: 1.5, marginBottom: 2 }}>{s.authors}</div>
                        <div style={{ fontSize: 8.5, color: C.t4, lineHeight: 1.5 }}>{s.desc}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div style={{ width: '100%', maxWidth: 720, background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 8, padding: '20px 24px' }}>
              <div style={{ ...headStyle, marginBottom: 14 }}>◉ What This Module Does</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                {[
                  { icon: '🏗️', title: 'Structure Detection', desc: 'Sections, hierarchy, reading flow — pure computation' },
                  { icon: '🧬', title: 'Concept Extraction', desc: 'Key terms via TF-IDF, co-occurrence graphs, n-gram analysis' },
                  { icon: '⚙️', title: 'Methodology', desc: '12 research method taxonomies detected via keyword patterns' },
                  { icon: '📊', title: 'Claims & Results', desc: 'Statistical claims, percentages, SOTA assertions auto-detected' },
                  { icon: '🔗', title: 'Citation Network', desc: 'Reference extraction, inline citation frequency, DOI detection' },
                  { icon: '🔬', title: 'Reproducibility', desc: '8-factor algorithmic scoring: code, data, hyperparams, baselines' },
                  { icon: '🎯', title: 'Critique Engine', desc: 'Strengths, weaknesses, assumptions — structural analysis' },
                  { icon: '🧠', title: 'AI Enhancement', desc: 'Add an LLM key after upload for deep summaries & expert critique' },
                ].map((f, i) => (
                  <div key={i} style={{ ...metricBox(PALETTE[i % PALETTE.length]), display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 18 }}>{f.icon}</span>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: C.t1, marginBottom: 3 }}>{f.title}</div>
                      <div style={{ fontSize: 9, color: C.t3, lineHeight: 1.5 }}>{f.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── STRUCTURE TAB ──────────────────────────────────────────────────── */}
        {tab === 'structure' && analysis && <StructureTab analysis={analysis} rawText={rawText} ai={aiData?.extract} />}

        {/* ── CONCEPTS TAB ───────────────────────────────────────────────────── */}
        {tab === 'concepts' && analysis && <ConceptsTab analysis={analysis} ai={aiData?.concepts} />}

        {/* ── METHODOLOGY TAB ────────────────────────────────────────────────── */}
        {tab === 'methodology' && analysis && <MethodologyTab analysis={analysis} ai={aiData?.methodology} />}

        {/* ── RESULTS TAB ────────────────────────────────────────────────────── */}
        {tab === 'results' && analysis && <ResultsTab analysis={analysis} ai={aiData?.results} />}

        {/* ── CITATIONS TAB ──────────────────────────────────────────────────── */}
        {tab === 'citations' && analysis && <CitationsTab analysis={analysis} />}

        {/* ── CONNECTIONS TAB ────────────────────────────────────────────────── */}
        {tab === 'connections' && analysis && <ConnectionsTab analysis={analysis} ai={aiData?.connections} />}

        {/* ── REPRODUCE TAB ──────────────────────────────────────────────────── */}
        {tab === 'reproduce' && analysis && <ReproduceTab analysis={analysis} ai={aiData?.methodology} />}

        {/* ── CRITIQUE TAB ───────────────────────────────────────────────────── */}
        {tab === 'critique' && analysis && <CritiqueTab analysis={analysis} ai={aiData?.critique} />}

        {/* ── REPORT TAB ─────────────────────────────────────────────────────── */}
        {tab === 'report' && analysis && <ReportTab analysis={analysis} aiData={aiData} file={file} rawText={rawText} authUser={authUser} isAuthenticated={isAuthenticated} log={log} />}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

// ── Log Panel ─────────────────────────────────────────────────────────────────
function LogPanel({ logs }) {
  const ref = useRef(null);
  useEffect(() => { ref.current?.scrollTo(0, ref.current.scrollHeight); }, [logs]);
  const colors = { err: C.red, ok: C.green, warn: C.gold, info: C.t3 };
  return (
    <div style={{ ...panelStyle, maxHeight: 140, overflow: 'hidden' }}>
      <div style={headStyle}>◎ Log</div>
      <div ref={ref} style={{ maxHeight: 100, overflowY: 'auto', fontSize: 10, lineHeight: 1.7, fontFamily: FONT }}>
        {logs.map((l, i) => (
          <div key={i} style={{ color: colors[l.t] || C.t3 }}>
            <span style={{ color: C.t4, marginRight: 6 }}>[{l.ts}]</span>{l.msg}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── AI Insight Panel (shared) ─────────────────────────────────────────────────
function AiInsight({ label, children }) {
  if (!children) return null;
  return (
    <div style={{ ...panelStyle, borderLeft: `3px solid ${C.green}`, background: `${C.green}04` }}>
      <div style={{ ...headStyle, color: C.green }}>🧠 {label}</div>
      {children}
    </div>
  );
}

// ── STRUCTURE TAB ─────────────────────────────────────────────────────────────
function StructureTab({ analysis, rawText, ai }) {
  const { sections, metadata, readability } = analysis;

  // Section word distribution for chart
  const sectionData = sections.filter(s => s.wordCount > 5).map((s, i) => ({
    name: s.title?.substring(0, 25) || `Section ${i + 1}`,
    words: s.wordCount,
    type: s.type,
    fill: PALETTE[i % PALETTE.length],
  }));

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      {/* Paper identity */}
      <div style={panelStyle}>
        <div style={headStyle}>◈ Paper Identity</div>
        <div style={{ fontSize: 16, fontWeight: 800, color: C.t1, marginBottom: 8, lineHeight: 1.4 }}>
          {metadata.title}
        </div>
        {metadata.abstract && (
          <div style={{ fontSize: 11, color: C.t2, lineHeight: 1.7, borderLeft: `2px solid ${C.cyan}40`, paddingLeft: 12, marginBottom: 12 }}>
            {metadata.abstract.substring(0, 500)}{metadata.abstract.length > 500 ? '...' : ''}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {metadata.pubYear && <span style={tagStyle(C.gold)}>Year: {metadata.pubYear}</span>}
          <span style={tagStyle(C.cyan)}>{metadata.wordCount.toLocaleString()} words</span>
          <span style={tagStyle(C.green)}>~{metadata.estimatedPages} pages</span>
          <span style={tagStyle(C.violet)}>~{metadata.readingTimeMin} min read</span>
          <span style={tagStyle(C.orange)}>{sections.length} sections</span>
        </div>
      </div>

      {/* Quick metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8, marginBottom: 12 }}>
        {[
          { label: 'Readability', value: readability.fleschReadingEase, color: C.cyan, sub: readability.difficultyLabel },
          { label: 'Grade Level', value: readability.fleschKincaidGrade, color: C.violet, sub: readability.gradeLabel },
          { label: 'Complexity', value: `${readability.complexityScore}/100`, color: C.orange, sub: 'Technical density' },
          { label: 'Vocab Richness', value: `${(readability.vocabularyRichness * 100).toFixed(0)}%`, color: C.green, sub: 'Type-token ratio' },
        ].map((m, i) => (
          <div key={i} style={metricBox(m.color)}>
            <div style={smallLabel}>{m.label}</div>
            <div style={bigVal(m.color)}>{m.value}</div>
            <div style={{ fontSize: 9, color: C.t3, marginTop: 2 }}>{m.sub}</div>
          </div>
        ))}
      </div>

      {/* Section structure map */}
      <div style={panelStyle}>
        <div style={headStyle}>🏗️ Section Structure</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {sections.map((s, i) => {
            const pct = Math.min(100, (s.wordCount / Math.max(...sections.map(x => x.wordCount), 1)) * 100);
            const color = PALETTE[i % PALETTE.length];
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 180, fontSize: 10, color: C.t2, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.title?.substring(0, 30)}
                </div>
                <div style={{ flex: 1, background: C.b0, borderRadius: 3, height: 14, overflow: 'hidden' }}>
                  <div style={{ background: `${color}60`, height: '100%', width: `${pct}%`, borderRadius: 3, transition: 'width .3s' }} />
                </div>
                <div style={{ width: 60, fontSize: 9, color: C.t3, textAlign: 'right' }}>{s.wordCount}w</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Section word distribution chart */}
      {sectionData.length > 0 && (
        <div style={panelStyle}>
          <div style={headStyle}>📊 Section Word Distribution</div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={sectionData} margin={{ top: 5, right: 10, left: 10, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.b0} />
              <XAxis dataKey="name" angle={-35} textAnchor="end" tick={{ fill: C.t3, fontSize: 8, fontFamily: FONT }} height={60} />
              <YAxis tick={{ fill: C.t3, fontSize: 9, fontFamily: FONT }} />
              <Tooltip {...chartTooltip} />
              <Bar dataKey="words" radius={[3, 3, 0, 0]}>
                {sectionData.map((d, i) => <Cell key={i} fill={d.fill} fillOpacity={0.7} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Readability breakdown */}
      <div style={panelStyle}>
        <div style={headStyle}>📐 Readability Profile</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 6 }}>
          {[
            { label: 'Avg Sentence Length', value: `${readability.avgSentenceLength} words` },
            { label: 'Avg Word Length', value: `${readability.avgWordLength} chars` },
            { label: 'Syllables/Word', value: readability.syllablesPerWord },
            { label: 'Long Words (7+ chars)', value: `${readability.longWordRatio}%` },
            { label: 'Technical Density', value: `${readability.technicalDensity}%` },
            { label: 'Total Sentences', value: readability.totalSentences },
          ].map((m, i) => (
            <div key={i} style={{ background: C.surface, padding: 8, borderRadius: 3, border: `1px solid ${C.b0}` }}>
              <div style={smallLabel}>{m.label}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.t1, marginTop: 4 }}>{m.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── AI-Enhanced: Paper Identity ────────────────────────────────────── */}
      {ai && (
        <AiInsight label="AI-Enhanced Paper Understanding">
          {ai.title && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.t1, marginBottom: 4 }}>{ai.title}</div>
              {ai.authors?.length > 0 && <div style={{ fontSize: 10, color: C.t2, marginBottom: 2 }}>👤 {ai.authors.join(', ')}</div>}
              {ai.affiliations?.length > 0 && <div style={{ fontSize: 9, color: C.t3, marginBottom: 2 }}>🏛️ {ai.affiliations.join('; ')}</div>}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                {ai.venue && <span style={tagStyle(C.gold)}>{ai.venue}</span>}
                {ai.year && <span style={tagStyle(C.cyan)}>{ai.year}</span>}
                {ai.paper_type && <span style={tagStyle(C.violet)}>{ai.paper_type}</span>}
                {ai.domain && <span style={tagStyle(C.orange)}>{ai.domain}</span>}
              </div>
            </div>
          )}
          {ai.problem_statement && (
            <div style={{ fontSize: 11, color: C.t2, lineHeight: 1.7, padding: '8px 12px', background: C.surface, borderRadius: 3, borderLeft: `2px solid ${C.cyan}40`, marginBottom: 10 }}>
              <div style={smallLabel}>Problem Statement</div>
              {ai.problem_statement}
            </div>
          )}
          {ai.contributions?.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={smallLabel}>Key Contributions</div>
              {ai.contributions.map((c, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '6px 10px', marginBottom: 3, background: C.surface, borderRadius: 3, borderLeft: `3px solid ${c.novelty === 'high' ? C.green : c.novelty === 'medium' ? C.gold : C.t3}` }}>
                  <span style={tagStyle(C.violet)}>{c.type}</span>
                  <span style={{ fontSize: 10, color: C.t2, lineHeight: 1.5, flex: 1 }}>{c.text}</span>
                  <span style={tagStyle(c.novelty === 'high' ? C.green : c.novelty === 'medium' ? C.gold : C.t3)}>{c.novelty}</span>
                </div>
              ))}
            </div>
          )}
          {ai.sections?.length > 0 && (
            <div>
              <div style={smallLabel}>Section Summaries (AI)</div>
              {ai.sections.map((s, i) => (
                <div key={i} style={{ padding: '6px 10px', marginBottom: 3, background: C.surface, borderRadius: 3, border: `1px solid ${C.b0}` }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: PALETTE[i % PALETTE.length], marginBottom: 2 }}>
                    {s.number ? `${s.number}. ` : ''}{s.title} <span style={{ ...tagStyle(C.cyan), marginLeft: 6 }}>{s.role}</span>
                  </div>
                  <div style={{ fontSize: 9, color: C.t3, lineHeight: 1.6 }}>{s.summary}</div>
                </div>
              ))}
            </div>
          )}
        </AiInsight>
      )}
    </div>
  );
}

// ── CONCEPTS TAB ──────────────────────────────────────────────────────────────
function ConceptsTab({ analysis, ai }) {
  const { concepts } = analysis;
  const topConcepts = concepts.concepts.slice(0, 30);

  // Treemap data
  const treemapData = topConcepts.slice(0, 20).map((c, i) => ({
    name: c.term, size: c.freq, fill: PALETTE[i % PALETTE.length],
  }));

  // Co-occurrence links
  const coLinks = [];
  const coMatrix = concepts.cooccurrence;
  const topTerms = topConcepts.slice(0, 15).map(c => c.term);
  for (let i = 0; i < topTerms.length; i++) {
    for (let j = i + 1; j < topTerms.length; j++) {
      const w = coMatrix[topTerms[i]]?.[topTerms[j]] || 0;
      if (w >= 2) coLinks.push({ from: topTerms[i], to: topTerms[j], weight: w });
    }
  }
  coLinks.sort((a, b) => b.weight - a.weight);

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      {/* Concept stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8, marginBottom: 12 }}>
        <MetricCard label="Key Concepts" value={concepts.concepts.length} color={C.violet} sub="Extracted" />
        <MetricCard label="Vocabulary" value={concepts.vocabularySize.toLocaleString()} color={C.cyan} sub="Unique tokens" />
        <MetricCard label="Total Tokens" value={concepts.totalTokens.toLocaleString()} color={C.green} sub="After cleaning" />
        <MetricCard label="Top Bigrams" value={concepts.topBigrams.length} color={C.orange} sub="Two-word phrases" />
      </div>

      {/* Top concepts ranked */}
      <div style={panelStyle}>
        <div style={headStyle}>🧬 Key Concepts (ranked by importance)</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {topConcepts.map((c, i) => {
            const opacity = 0.4 + (c.score / (topConcepts[0]?.score || 1)) * 0.6;
            return (
              <span key={i} style={{
                ...tagStyle(PALETTE[i % PALETTE.length]),
                opacity, fontSize: 9 + Math.min(4, c.score / (topConcepts[0]?.score || 1) * 4),
              }}>
                {c.term} <span style={{ opacity: 0.6 }}>×{c.freq}</span>
              </span>
            );
          })}
        </div>
      </div>

      {/* Concept frequency chart */}
      <div style={panelStyle}>
        <div style={headStyle}>📊 Concept Frequency</div>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={topConcepts.slice(0, 20)} layout="vertical" margin={{ left: 120, right: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.b0} />
            <XAxis type="number" tick={{ fill: C.t3, fontSize: 9, fontFamily: FONT }} />
            <YAxis dataKey="term" type="category" tick={{ fill: C.t2, fontSize: 9, fontFamily: FONT }} width={110} />
            <Tooltip {...chartTooltip} />
            <Bar dataKey="freq" radius={[0, 3, 3, 0]}>
              {topConcepts.slice(0, 20).map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} fillOpacity={0.7} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* N-gram breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div style={panelStyle}>
          <div style={headStyle}>Bigrams (2-word phrases)</div>
          {concepts.topBigrams.slice(0, 10).map((b, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 10, color: C.t2, borderBottom: `1px solid ${C.b0}` }}>
              <span>{b.term}</span>
              <span style={{ color: C.violet, fontWeight: 700 }}>×{b.freq}</span>
            </div>
          ))}
        </div>
        <div style={panelStyle}>
          <div style={headStyle}>Trigrams (3-word phrases)</div>
          {concepts.topTrigrams.slice(0, 10).map((b, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 10, color: C.t2, borderBottom: `1px solid ${C.b0}` }}>
              <span>{b.term}</span>
              <span style={{ color: C.orange, fontWeight: 700 }}>×{b.freq}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Co-occurrence network (table form) */}
      {coLinks.length > 0 && (
        <div style={panelStyle}>
          <div style={headStyle}>🔗 Concept Co-occurrence (top pairs)</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6 }}>
            {coLinks.slice(0, 15).map((link, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: C.t2, padding: '4px 8px', background: C.surface, borderRadius: 3, border: `1px solid ${C.b0}` }}>
                <span style={tagStyle(C.cyan)}>{link.from}</span>
                <span style={{ color: C.t4 }}>↔</span>
                <span style={tagStyle(C.green)}>{link.to}</span>
                <span style={{ marginLeft: 'auto', color: C.gold, fontWeight: 700, fontSize: 9 }}>×{link.weight}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── AI-Enhanced Concepts ──────────────────────────────────────────── */}
      {ai && (
        <AiInsight label="AI-Enhanced Concept Analysis">
          {ai.core_concepts?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={smallLabel}>Key Concepts with Explanations</div>
              {ai.core_concepts.map((c, i) => (
                <div key={i} style={{ padding: '8px 10px', marginBottom: 4, background: C.surface, borderRadius: 3, border: `1px solid ${C.b0}`, borderLeft: `3px solid ${c.importance === 'high' ? C.green : c.importance === 'medium' ? C.gold : C.t3}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: C.t1 }}>{c.term}</span>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <span style={tagStyle(C.violet)}>{c.category}</span>
                      {c.is_novel && <span style={tagStyle(C.green)}>NOVEL</span>}
                    </div>
                  </div>
                  <div style={{ fontSize: 10, color: C.t3, lineHeight: 1.6 }}>{c.definition}</div>
                </div>
              ))}
            </div>
          )}
          {ai.novel_contributions?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={smallLabel}>Novel Contributions</div>
              {ai.novel_contributions.map((n, i) => (
                <div key={i} style={{ padding: '8px 10px', marginBottom: 4, background: `${C.green}06`, borderRadius: 3, borderLeft: `3px solid ${C.green}` }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.t1, marginBottom: 3 }}>{n.what}</div>
                  <div style={{ fontSize: 9, color: C.t3, lineHeight: 1.6 }}>
                    <strong style={{ color: C.t2 }}>Why novel:</strong> {n.why_novel}
                  </div>
                  <div style={{ fontSize: 9, color: C.t3, lineHeight: 1.6 }}>
                    <strong style={{ color: C.t2 }}>Impact:</strong> {n.impact}
                  </div>
                </div>
              ))}
            </div>
          )}
          {ai.core_equations?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={smallLabel}>Core Equations</div>
              {ai.core_equations.map((eq, i) => (
                <div key={i} style={{ padding: '6px 10px', marginBottom: 3, background: C.surface, borderRadius: 3, border: `1px solid ${C.b0}` }}>
                  <span style={{ fontWeight: 700, color: C.cyan, fontSize: 10, marginRight: 8 }}>{eq.name}</span>
                  <span style={{ fontSize: 9, color: C.t3 }}>{eq.purpose}</span>
                  {eq.significance && <div style={{ fontSize: 9, color: C.gold, marginTop: 2 }}>↳ {eq.significance}</div>}
                </div>
              ))}
            </div>
          )}
          {ai.domain_context && (
            <div style={{ fontSize: 10, color: C.t2, lineHeight: 1.7, padding: '8px 12px', background: C.surface, borderRadius: 3, borderLeft: `2px solid ${C.cyan}40` }}>
              <div style={smallLabel}>Field Context</div>
              {ai.domain_context}
            </div>
          )}
        </AiInsight>
      )}
    </div>
  );
}

// ── METHODOLOGY TAB ───────────────────────────────────────────────────────────
function MethodologyTab({ analysis, ai }) {
  const { methodology } = analysis;

  // Radar data for method confidence
  const radarData = methodology.methods.map(m => ({
    method: m.method.split(' ').slice(0, 2).join(' '),
    confidence: +(m.confidence * 100).toFixed(0),
    keywords: m.keywordCount,
  }));

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      {/* Primary method */}
      <div style={panelStyle}>
        <div style={headStyle}>⚙️ Primary Research Methodology</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: C.orange, marginBottom: 6 }}>
          {methodology.primaryMethod}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {methodology.isEmpirical && <span style={tagStyle(C.green)}>Empirical</span>}
          {methodology.isTheoretical && <span style={tagStyle(C.violet)}>Theoretical</span>}
          <span style={tagStyle(C.cyan)}>{methodology.methods.length} methods detected</span>
        </div>
      </div>

      {/* Method confidence radar */}
      {radarData.length > 0 && (
        <div style={panelStyle}>
          <div style={headStyle}>📡 Method Confidence Radar</div>
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={radarData}>
              <PolarGrid stroke={C.b1} />
              <PolarAngleAxis dataKey="method" tick={{ fill: C.t2, fontSize: 9, fontFamily: FONT }} />
              <PolarRadiusAxis tick={{ fill: C.t4, fontSize: 8 }} domain={[0, 100]} />
              <Radar name="Confidence %" dataKey="confidence" stroke={C.cyan} fill={C.cyan} fillOpacity={0.2} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Detected methods breakdown */}
      <div style={panelStyle}>
        <div style={headStyle}>🔍 Detected Methods</div>
        {methodology.methods.map((m, i) => (
          <div key={i} style={{ marginBottom: 12, padding: 10, background: C.surface, borderRadius: 4, border: `1px solid ${C.b0}`, borderLeft: `3px solid ${PALETTE[i % PALETTE.length]}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontWeight: 700, color: C.t1, fontSize: 12 }}>{m.method}</span>
              <span style={{ color: C.gold, fontSize: 10, fontWeight: 700 }}>{(m.confidence * 100).toFixed(0)}% confidence</span>
            </div>
            <div style={{ background: C.b0, borderRadius: 3, height: 6, overflow: 'hidden', marginBottom: 6 }}>
              <div style={{ background: PALETTE[i % PALETTE.length], height: '100%', width: `${m.confidence * 100}%`, borderRadius: 3 }} />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {m.matchedKeywords.map((kw, j) => (
                <span key={j} style={tagStyle(PALETTE[i % PALETTE.length])}>{kw}</span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Architectures & Datasets */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div style={panelStyle}>
          <div style={headStyle}>🏛️ Architectures Mentioned</div>
          {methodology.architectures.length === 0 && <div style={{ fontSize: 10, color: C.t4 }}>None detected</div>}
          {methodology.architectures.map((a, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: `1px solid ${C.b0}`, fontSize: 10, color: C.t2 }}>
              <span style={{ fontWeight: 600 }}>{a.name}</span>
              <span style={{ color: C.cyan }}>×{a.mentions}</span>
            </div>
          ))}
        </div>
        <div style={panelStyle}>
          <div style={headStyle}>📁 Datasets Mentioned</div>
          {methodology.datasets.length === 0 && <div style={{ fontSize: 10, color: C.t4 }}>None detected</div>}
          {methodology.datasets.map((d, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: `1px solid ${C.b0}`, fontSize: 10, color: C.t2 }}>
              <span style={{ fontWeight: 600 }}>{d.name}</span>
              <span style={{ color: C.green }}>×{d.mentions}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Metrics */}
      <div style={panelStyle}>
        <div style={headStyle}>📏 Evaluation Metrics Used</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {methodology.metrics.length === 0 && <div style={{ fontSize: 10, color: C.t4 }}>None detected</div>}
          {methodology.metrics.map((m, i) => (
            <span key={i} style={tagStyle(PALETTE[i % PALETTE.length])}>
              {m.name} <span style={{ opacity: 0.6 }}>×{m.mentions}</span>
            </span>
          ))}
        </div>
      </div>

      {/* ── AI-Enhanced Methodology ───────────────────────────────────────── */}
      {ai && (
        <AiInsight label="AI-Enhanced Methodology Analysis">
          {ai.approach_summary && (
            <div style={{ fontSize: 11, color: C.t2, lineHeight: 1.7, padding: '8px 12px', background: C.surface, borderRadius: 3, borderLeft: `2px solid ${C.orange}40`, marginBottom: 10 }}>
              {ai.approach_summary}
            </div>
          )}
          {ai.key_innovations?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={smallLabel}>Key Innovations</div>
              {ai.key_innovations.map((inn, i) => (
                <div key={i} style={{ padding: '8px 10px', marginBottom: 4, background: `${C.green}06`, borderRadius: 3, borderLeft: `3px solid ${C.green}` }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.t1, marginBottom: 3 }}>{inn.what}</div>
                  <div style={{ fontSize: 9, color: C.t3, lineHeight: 1.6 }}><strong style={{ color: C.t2 }}>How:</strong> {inn.how}</div>
                  <div style={{ fontSize: 9, color: C.t3, lineHeight: 1.6 }}><strong style={{ color: C.t2 }}>Why:</strong> {inn.why}</div>
                </div>
              ))}
            </div>
          )}
          {ai.architecture?.components?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={smallLabel}>Architecture: {ai.architecture.name}</div>
              {ai.architecture.data_flow && (
                <div style={{ fontSize: 9, color: C.t3, marginBottom: 6, lineHeight: 1.5, fontStyle: 'italic' }}>{ai.architecture.data_flow}</div>
              )}
              {ai.architecture.components.map((comp, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, padding: '5px 10px', marginBottom: 3, background: C.surface, borderRadius: 3, border: `1px solid ${C.b0}` }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: PALETTE[i % PALETTE.length], minWidth: 100 }}>{comp.name}</span>
                  <span style={{ fontSize: 9, color: C.t2 }}>{comp.role}</span>
                </div>
              ))}
            </div>
          )}
          {ai.training_details && (ai.training_details.optimizer || ai.training_details.hardware) && (
            <div style={{ marginBottom: 10 }}>
              <div style={smallLabel}>Training Details</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 6 }}>
                {[
                  { label: 'Optimizer', val: ai.training_details.optimizer },
                  { label: 'Learning Rate', val: ai.training_details.learning_rate },
                  { label: 'Batch Size', val: ai.training_details.batch_size },
                  { label: 'Epochs', val: ai.training_details.epochs },
                  { label: 'Hardware', val: ai.training_details.hardware },
                  { label: 'Training Time', val: ai.training_details.training_time },
                ].filter(d => d.val).map((d, i) => (
                  <div key={i} style={{ background: C.surface, padding: 6, borderRadius: 3, border: `1px solid ${C.b0}` }}>
                    <div style={smallLabel}>{d.label}</div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: C.t1, marginTop: 2 }}>{d.val}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {ai.baselines?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={smallLabel}>Baselines Compared</div>
              {ai.baselines.map((b, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, padding: '5px 10px', marginBottom: 3, background: C.surface, borderRadius: 3, border: `1px solid ${C.b0}` }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: C.t2, minWidth: 100 }}>{b.name}</span>
                  <span style={{ fontSize: 9, color: C.t3, flex: 1 }}>{b.what}</span>
                  {b.result_vs_proposed && <span style={{ fontSize: 9, color: C.gold }}>{b.result_vs_proposed}</span>}
                </div>
              ))}
            </div>
          )}
          {ai.ablation_studies?.length > 0 && (
            <div>
              <div style={smallLabel}>Ablation Studies</div>
              {ai.ablation_studies.map((ab, i) => (
                <div key={i} style={{ padding: '5px 10px', marginBottom: 3, background: C.surface, borderRadius: 3, border: `1px solid ${C.b0}` }}>
                  <span style={{ fontSize: 10, fontWeight: 600, color: C.t2 }}>{ab.component}: </span>
                  <span style={{ fontSize: 9, color: C.t3 }}>{ab.finding}</span>
                </div>
              ))}
            </div>
          )}
        </AiInsight>
      )}
    </div>
  );
}

// ── RESULTS TAB ───────────────────────────────────────────────────────────────
function ResultsTab({ analysis, ai }) {
  const { claims, methodology } = analysis;

  // Claims by type chart
  const claimsByType = [
    { name: 'Percentage', count: claims.percentageClaims, fill: C.cyan },
    { name: 'Comparison', count: claims.comparisonClaims, fill: C.green },
    { name: 'Metric', count: claims.metricClaims, fill: C.violet },
    { name: 'SOTA', count: claims.sotaClaims, fill: C.gold },
  ].filter(d => d.count > 0);

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      {/* Claims overview */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8, marginBottom: 12 }}>
        <MetricCard label="Total Claims" value={claims.totalClaims} color={C.cyan} sub="Auto-detected" />
        <MetricCard label="% Claims" value={claims.percentageClaims} color={C.green} sub="Percentage results" />
        <MetricCard label="Comparisons" value={claims.comparisonClaims} color={C.violet} sub="Relative gains" />
        <MetricCard label="SOTA Claims" value={claims.sotaClaims} color={C.gold} sub="State-of-the-art" />
      </div>

      {/* Claims distribution */}
      {claimsByType.length > 0 && (
        <div style={panelStyle}>
          <div style={headStyle}>📊 Claims Distribution</div>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={claimsByType} cx="50%" cy="50%" outerRadius={70} dataKey="count" label={({ name, count }) => `${name}: ${count}`}>
                {claimsByType.map((d, i) => <Cell key={i} fill={d.fill} />)}
              </Pie>
              <Tooltip {...chartTooltip} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* All claims timeline */}
      <div style={panelStyle}>
        <div style={headStyle}>🔎 Detected Claims (in order of appearance)</div>
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          {claims.claims.length === 0 && <div style={{ fontSize: 10, color: C.t4 }}>No statistical claims detected.</div>}
          {claims.claims.map((c, i) => (
            <div key={i} style={{ padding: '8px 10px', marginBottom: 4, background: C.surface, borderRadius: 3, border: `1px solid ${C.b0}`, borderLeft: `3px solid ${c.type === 'sota_claim' ? C.gold : c.type === 'percentage' ? C.cyan : c.type === 'comparison' ? C.green : C.violet}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={tagStyle(c.type === 'sota_claim' ? C.gold : C.cyan)}>{c.type.replace('_', ' ').toUpperCase()}</span>
                {c.value != null && <span style={{ fontSize: 14, fontWeight: 800, color: C.green }}>{c.value}{c.type === 'percentage' || c.type === 'comparison' ? '%' : ''}</span>}
              </div>
              <div style={{ fontSize: 10, color: C.t3, lineHeight: 1.5, fontStyle: 'italic' }}>
                "...{c.context}..."
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── AI-Enhanced Results ───────────────────────────────────────────── */}
      {ai && (
        <AiInsight label="AI-Enhanced Results Analysis">
          {ai.main_finding && (
            <div style={{ fontSize: 12, color: C.t1, lineHeight: 1.7, padding: '10px 14px', background: `${C.green}08`, borderRadius: 4, borderLeft: `3px solid ${C.green}`, marginBottom: 10, fontWeight: 600 }}>
              {ai.main_finding}
            </div>
          )}
          {ai.quantitative_results?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={smallLabel}>Quantitative Results (AI-Extracted)</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, fontFamily: FONT }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${C.b1}` }}>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: C.t3, fontWeight: 600 }}>Metric</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: C.t3, fontWeight: 600 }}>Value</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: C.t3, fontWeight: 600 }}>Dataset</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: C.t3, fontWeight: 600 }}>Comparison</th>
                      <th style={{ textAlign: 'center', padding: '6px 8px', color: C.t3, fontWeight: 600 }}>SOTA?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ai.quantitative_results.map((r, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${C.b0}`, background: r.is_sota ? `${C.gold}08` : 'transparent' }}>
                        <td style={{ padding: '5px 8px', color: C.t1, fontWeight: 600 }}>{r.metric}</td>
                        <td style={{ padding: '5px 8px', color: C.green, fontWeight: 800 }}>{r.value}</td>
                        <td style={{ padding: '5px 8px', color: C.t2 }}>{r.dataset}</td>
                        <td style={{ padding: '5px 8px', color: C.t3, fontSize: 9 }}>{r.comparison}</td>
                        <td style={{ padding: '5px 8px', textAlign: 'center' }}>{r.is_sota ? '🏆' : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {ai.performance_claims?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={smallLabel}>Performance Claims</div>
              {ai.performance_claims.map((p, i) => (
                <div key={i} style={{ padding: '6px 10px', marginBottom: 3, background: C.surface, borderRadius: 3, borderLeft: `3px solid ${p.evidence_strength === 'strong' ? C.green : p.evidence_strength === 'moderate' ? C.gold : C.red}` }}>
                  <div style={{ fontSize: 10, color: C.t2, lineHeight: 1.5 }}>{p.claim}</div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 3 }}>
                    <span style={tagStyle(C.violet)}>{p.type}</span>
                    <span style={tagStyle(p.evidence_strength === 'strong' ? C.green : C.gold)}>{p.evidence_strength} evidence</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          {ai.limitations_acknowledged?.length > 0 && (
            <div>
              <div style={smallLabel}>Acknowledged Limitations</div>
              {ai.limitations_acknowledged.map((l, i) => (
                <div key={i} style={{ padding: '5px 10px', marginBottom: 3, background: C.surface, borderRadius: 3, borderLeft: `3px solid ${l.severity === 'high' ? C.red : C.gold}` }}>
                  <span style={{ fontSize: 10, color: C.t2 }}>{l.limitation}</span>
                  <span style={{ ...tagStyle(l.severity === 'high' ? C.red : C.gold), marginLeft: 8 }}>{l.severity}</span>
                </div>
              ))}
            </div>
          )}
        </AiInsight>
      )}
    </div>
  );
}

// ── CITATIONS TAB ─────────────────────────────────────────────────────────────
function CitationsTab({ analysis }) {
  const { citations } = analysis;

  // Most cited refs chart
  const mostCitedData = citations.mostCited.map(([id, count]) => ({
    name: `[${id}]`,
    citations: count,
  }));

  // Year distribution
  const yearDist = {};
  for (const ref of citations.references) {
    if (ref.year) yearDist[ref.year] = (yearDist[ref.year] || 0) + 1;
  }
  const yearData = Object.entries(yearDist).sort((a, b) => a[0] - b[0]).map(([year, count]) => ({ year, count }));

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      {/* Citation stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8, marginBottom: 12 }}>
        <MetricCard label="References" value={citations.totalRefs} color={C.gold} sub="Extracted" />
        <MetricCard label="Inline Citations" value={citations.inlineCitations} color={C.cyan} sub="Bracket style" />
        <MetricCard label="Author Citations" value={citations.authorCitations.length} color={C.violet} sub="Name-year style" />
        <MetricCard label="DOIs Found" value={citations.dois.length} color={C.green} sub="Resolvable" />
      </div>

      {/* Most cited references */}
      {mostCitedData.length > 0 && (
        <div style={panelStyle}>
          <div style={headStyle}>🔥 Most Cited References</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={mostCitedData}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.b0} />
              <XAxis dataKey="name" tick={{ fill: C.t3, fontSize: 10, fontFamily: FONT }} />
              <YAxis tick={{ fill: C.t3, fontSize: 9, fontFamily: FONT }} />
              <Tooltip {...chartTooltip} />
              <Bar dataKey="citations" fill={C.gold} radius={[3, 3, 0, 0]} fillOpacity={0.7} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Reference year distribution */}
      {yearData.length > 0 && (
        <div style={panelStyle}>
          <div style={headStyle}>📅 Reference Year Distribution</div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={yearData}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.b0} />
              <XAxis dataKey="year" tick={{ fill: C.t3, fontSize: 9, fontFamily: FONT }} />
              <YAxis tick={{ fill: C.t3, fontSize: 9, fontFamily: FONT }} />
              <Tooltip {...chartTooltip} />
              <Area type="monotone" dataKey="count" stroke={C.cyan} fill={C.cyan} fillOpacity={0.15} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Reference list */}
      <div style={panelStyle}>
        <div style={headStyle}>📚 Extracted References ({citations.totalRefs})</div>
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          {citations.references.length === 0 && <div style={{ fontSize: 10, color: C.t4 }}>No structured references detected. The paper may use inline author-year citations.</div>}
          {citations.references.map((ref, i) => (
            <div key={i} style={{ padding: '6px 8px', marginBottom: 3, background: C.surface, borderRadius: 3, border: `1px solid ${C.b0}`, fontSize: 10, color: C.t2, lineHeight: 1.5 }}>
              <span style={{ color: C.gold, fontWeight: 700, marginRight: 6 }}>[{ref.id}]</span>
              {ref.authors && <span style={{ color: C.t1 }}>{ref.authors}. </span>}
              {ref.title && <span style={{ fontStyle: 'italic' }}>{ref.title}. </span>}
              {ref.year && <span style={{ color: C.cyan }}>({ref.year})</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── CONNECTIONS TAB ───────────────────────────────────────────────────────────
function ConnectionsTab({ analysis, ai }) {
  const { methodology, concepts, citations, readability } = analysis;

  // Research field mapping based on detected methods + concepts
  const fields = [];
  const methodSet = new Set(methodology.methods.map(m => m.method));

  if (methodSet.has('Deep Learning') || methodSet.has('Natural Language Processing')) fields.push({ field: 'AI / Machine Learning', strength: 'High', color: C.cyan });
  if (methodSet.has('Computer Vision')) fields.push({ field: 'Computer Vision', strength: 'High', color: C.pink });
  if (methodSet.has('Natural Language Processing')) fields.push({ field: 'Natural Language Processing', strength: 'High', color: C.violet });
  if (methodSet.has('Reinforcement Learning')) fields.push({ field: 'Reinforcement Learning', strength: 'High', color: C.green });
  if (methodSet.has('Generative Models')) fields.push({ field: 'Generative AI', strength: 'High', color: C.gold });
  if (methodSet.has('Statistical Analysis')) fields.push({ field: 'Statistics / Data Science', strength: 'Medium', color: C.blue });
  if (methodSet.has('Graph Methods')) fields.push({ field: 'Graph Learning', strength: 'Medium', color: C.orange });
  if (methodSet.has('Optimization')) fields.push({ field: 'Optimization Theory', strength: 'Medium', color: C.red });
  if (methodSet.has('Transfer Learning')) fields.push({ field: 'Transfer / Few-shot Learning', strength: 'Medium', color: C.cyan });
  if (fields.length === 0) fields.push({ field: 'General Research', strength: 'Low', color: C.t3 });

  // Impact indicators (heuristic-based)
  const indicators = [];
  if (citations.totalRefs > 40) indicators.push({ label: 'Extensive Literature Review', detail: `${citations.totalRefs} references ground this in established work`, type: 'strength' });
  if (citations.totalRefs < 10 && citations.totalRefs > 0) indicators.push({ label: 'Limited References', detail: `Only ${citations.totalRefs} references — may miss key related work`, type: 'concern' });
  if (methodology.methods.length >= 3) indicators.push({ label: 'Multi-method Approach', detail: `${methodology.methods.length} distinct methodologies create robust framework`, type: 'strength' });
  if (methodology.architectures.length > 0) indicators.push({ label: 'Named Architectures', detail: `References ${methodology.architectures.map(a => a.name).join(', ')}`, type: 'context' });
  if (methodology.datasets.length > 0) indicators.push({ label: 'Standard Benchmarks', detail: `Evaluated on ${methodology.datasets.map(d => d.name).join(', ')}`, type: 'strength' });
  if (readability.complexityScore > 70) indicators.push({ label: 'High Technical Complexity', detail: 'Requires strong domain background to fully understand', type: 'context' });

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      {/* Research field mapping */}
      <div style={panelStyle}>
        <div style={headStyle}>🌐 Research Field Mapping</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
          {fields.map((f, i) => (
            <div key={i} style={metricBox(f.color)}>
              <div style={{ fontSize: 12, fontWeight: 700, color: f.color }}>{f.field}</div>
              <div style={{ fontSize: 9, color: C.t3, marginTop: 3 }}>Connection: {f.strength}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Research positioning indicators */}
      <div style={panelStyle}>
        <div style={headStyle}>🎯 Research Positioning</div>
        {indicators.map((ind, i) => (
          <div key={i} style={{
            padding: '8px 12px', marginBottom: 6, background: C.surface, borderRadius: 3,
            border: `1px solid ${C.b0}`,
            borderLeft: `3px solid ${ind.type === 'strength' ? C.green : ind.type === 'concern' ? C.gold : C.cyan}`,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: C.t1 }}>{ind.label}</span>
              <span style={tagStyle(ind.type === 'strength' ? C.green : ind.type === 'concern' ? C.gold : C.cyan)}>
                {ind.type}
              </span>
            </div>
            <div style={{ fontSize: 10, color: C.t3 }}>{ind.detail}</div>
          </div>
        ))}
      </div>

      {/* Cross-domain concept bridges */}
      <div style={panelStyle}>
        <div style={headStyle}>🌉 Cross-Domain Concept Bridges</div>
        <div style={{ fontSize: 10, color: C.t3, lineHeight: 1.7 }}>
          Key concepts from this paper that bridge multiple research areas:
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {concepts.concepts.slice(0, 15).map((c, i) => (
            <div key={i} style={{ ...tagStyle(PALETTE[i % PALETTE.length]), cursor: 'default' }}>
              {c.term}
            </div>
          ))}
        </div>
      </div>

      {/* ── AI-Enhanced Connections ───────────────────────────────────────── */}
      {ai && (
        <AiInsight label="AI-Enhanced Research Connections">
          {ai.research_fields?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={smallLabel}>Research Fields (AI)</div>
              {ai.research_fields.map((f, i) => (
                <div key={i} style={{ padding: '6px 10px', marginBottom: 3, background: C.surface, borderRadius: 3, borderLeft: `3px solid ${f.relevance === 'primary' ? C.green : f.relevance === 'secondary' ? C.gold : C.t3}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: C.t1 }}>{f.field}</span>
                    <span style={tagStyle(f.relevance === 'primary' ? C.green : C.gold)}>{f.relevance}</span>
                  </div>
                  <div style={{ fontSize: 9, color: C.t3, lineHeight: 1.5, marginTop: 2 }}>{f.description}</div>
                </div>
              ))}
            </div>
          )}
          {ai.builds_upon?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={smallLabel}>Builds Upon</div>
              {ai.builds_upon.map((b, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, padding: '5px 10px', marginBottom: 3, background: C.surface, borderRadius: 3, border: `1px solid ${C.b0}` }}>
                  <span style={{ fontSize: 10, fontWeight: 600, color: C.t1, minWidth: 120 }}>{b.work}</span>
                  <span style={tagStyle(C.violet)}>{b.relationship}</span>
                  <span style={{ fontSize: 9, color: C.t3, flex: 1 }}>{b.detail}</span>
                </div>
              ))}
            </div>
          )}
          {ai.enables?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={smallLabel}>Enables / Future Directions</div>
              {ai.enables.map((e, i) => (
                <div key={i} style={{ padding: '6px 10px', marginBottom: 3, background: `${C.cyan}06`, borderRadius: 3, borderLeft: `3px solid ${C.cyan}` }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: C.t1 }}>{e.direction}</div>
                  <div style={{ fontSize: 9, color: C.t3, marginTop: 2 }}>{e.detail}</div>
                </div>
              ))}
            </div>
          )}
          {ai.paradigm_position && (
            <div style={{ fontSize: 10, color: C.t2, lineHeight: 1.7, padding: '8px 12px', background: C.surface, borderRadius: 3, borderLeft: `2px solid ${C.gold}40`, marginBottom: 10 }}>
              <div style={smallLabel}>Paradigm Position</div>
              {ai.paradigm_position}
            </div>
          )}
          {ai.industry_applications?.length > 0 && (
            <div>
              <div style={smallLabel}>Industry Applications</div>
              {ai.industry_applications.map((a, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, padding: '5px 10px', marginBottom: 3, background: C.surface, borderRadius: 3, border: `1px solid ${C.b0}` }}>
                  <span style={{ fontSize: 10, color: C.t1, flex: 1 }}>{a.application}</span>
                  <span style={tagStyle(a.readiness === 'ready' ? C.green : a.readiness === 'near-term' ? C.gold : C.t3)}>{a.readiness}</span>
                </div>
              ))}
            </div>
          )}
        </AiInsight>
      )}
    </div>
  );
}

// ── REPRODUCE TAB ─────────────────────────────────────────────────────────────
function ReproduceTab({ analysis, ai }) {
  const { reproducibility, methodology, claims } = analysis;

  // Generate experiment blueprint from detected methodology
  const blueprint = [];
  const methods = methodology.methods;

  if (methods.some(m => ['Supervised Learning', 'Deep Learning', 'Transfer Learning'].includes(m.method))) {
    blueprint.push({
      phase: 'Data Pipeline',
      steps: ['Obtain described datasets', 'Apply preprocessing (normalization, augmentation)', 'Split train/val/test per paper ratios', 'Verify data statistics match paper'],
      module: 'Auto EDA',
      color: C.cyan,
    });
  }
  if (methods.some(m => ['Supervised Learning', 'Statistical Analysis'].includes(m.method))) {
    blueprint.push({
      phase: 'ML Experiments',
      steps: ['Implement baseline models', 'Train with described hyperparameters', 'Run cross-validation', 'Compare metrics against reported values'],
      module: 'ML Experiments',
      color: C.orange,
    });
  }
  if (methods.some(m => ['Deep Learning', 'Computer Vision', 'Natural Language Processing', 'Generative Models'].includes(m.method))) {
    blueprint.push({
      phase: 'DL Experiments',
      steps: ['Build network architecture as described', 'Initialize weights per paper specs', 'Train with exact optimizer/scheduler', 'Reproduce ablation studies'],
      module: 'DL Experiments',
      color: C.green,
    });
  }
  if (blueprint.length === 0) {
    blueprint.push({
      phase: 'General Reproduction',
      steps: ['Identify core claims', 'Set up experimental environment', 'Implement described methodology', 'Validate results against reported metrics'],
      module: 'ML Experiments',
      color: C.violet,
    });
  }

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      {/* Reproducibility score */}
      <div style={panelStyle}>
        <div style={headStyle}>🔬 Reproducibility Assessment</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 12 }}>
          <div>
            <div style={bigVal(reproducibility.score >= 70 ? C.green : reproducibility.score >= 40 ? C.gold : C.red)}>
              {reproducibility.score}/{reproducibility.maxScore}
            </div>
            <div style={{ fontSize: 11, color: C.t2, fontWeight: 600, marginTop: 4 }}>{reproducibility.label}</div>
          </div>
          <div style={{ flex: 1, background: C.b0, borderRadius: 6, height: 12, overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 6,
              width: `${reproducibility.score}%`,
              background: reproducibility.score >= 70 ? C.green : reproducibility.score >= 40 ? C.gold : C.red,
              transition: 'width .5s',
            }} />
          </div>
        </div>

        {/* Factor breakdown */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 6 }}>
          {reproducibility.factors.map((f, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: C.surface, borderRadius: 3, border: `1px solid ${C.b0}` }}>
              <span style={{ fontSize: 10, color: C.t2 }}>✓ {f.label}</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: C.green }}>+{f.pts}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Experiment blueprint */}
      <div style={panelStyle}>
        <div style={headStyle}>📋 Reproduction Blueprint</div>
        <div style={{ fontSize: 10, color: C.t3, marginBottom: 12, lineHeight: 1.5 }}>
          Based on the detected methodology, here is a structured plan to reproduce this paper's experiments using the platform's modules:
        </div>
        {blueprint.map((phase, i) => (
          <div key={i} style={{ marginBottom: 14, padding: 12, background: C.surface, borderRadius: 4, border: `1px solid ${C.b0}`, borderLeft: `3px solid ${phase.color}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: C.t1 }}>Phase {i + 1}: {phase.phase}</span>
              <span style={tagStyle(phase.color)}>→ {phase.module}</span>
            </div>
            <ol style={{ margin: 0, paddingLeft: 18 }}>
              {phase.steps.map((s, j) => (
                <li key={j} style={{ fontSize: 10, color: C.t2, lineHeight: 1.8 }}>{s}</li>
              ))}
            </ol>
          </div>
        ))}
      </div>

      {/* Key claims to verify */}
      {claims.claims.length > 0 && (
        <div style={panelStyle}>
          <div style={headStyle}>✅ Key Claims to Verify</div>
          {claims.claims.filter(c => c.type === 'percentage' || c.type === 'metric').slice(0, 10).map((c, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', marginBottom: 3, background: C.surface, borderRadius: 3, border: `1px solid ${C.b0}` }}>
              <span style={{ width: 50, fontSize: 14, fontWeight: 800, color: C.green, textAlign: 'right' }}>
                {c.value}{c.type === 'percentage' ? '%' : ''}
              </span>
              <span style={{ fontSize: 10, color: C.t3 }}>{c.context.substring(0, 100)}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── AI-Enhanced Reproduction ─────────────────────────────────────── */}
      {ai && (
        <AiInsight label="AI-Enhanced Reproduction Guide">
          {ai.datasets?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={smallLabel}>Datasets (AI-Extracted)</div>
              {ai.datasets.map((d, i) => (
                <div key={i} style={{ padding: '6px 10px', marginBottom: 3, background: C.surface, borderRadius: 3, border: `1px solid ${C.b0}`, borderLeft: `3px solid ${C.cyan}` }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.t1 }}>{d.name} {d.size && <span style={{ color: C.t3, fontWeight: 400 }}>({d.size})</span>}</div>
                  <div style={{ fontSize: 9, color: C.t3, marginTop: 2 }}>{d.task}</div>
                  {d.split && <div style={{ fontSize: 9, color: C.gold, marginTop: 2 }}>Split: {d.split}</div>}
                </div>
              ))}
            </div>
          )}
          {ai.training_details && (ai.training_details.optimizer || ai.training_details.hardware) && (
            <div style={{ marginBottom: 10 }}>
              <div style={smallLabel}>Exact Training Recipe</div>
              <div style={{ padding: '8px 12px', background: C.surface, borderRadius: 3, border: `1px solid ${C.b0}`, fontFamily: FONT, fontSize: 10, lineHeight: 2, color: C.t2 }}>
                {ai.training_details.optimizer && <div>Optimizer: <b style={{ color: C.t1 }}>{ai.training_details.optimizer}</b></div>}
                {ai.training_details.learning_rate && <div>LR: <b style={{ color: C.t1 }}>{ai.training_details.learning_rate}</b></div>}
                {ai.training_details.batch_size && <div>Batch: <b style={{ color: C.t1 }}>{ai.training_details.batch_size}</b></div>}
                {ai.training_details.epochs && <div>Epochs: <b style={{ color: C.t1 }}>{ai.training_details.epochs}</b></div>}
                {ai.training_details.hardware && <div>Hardware: <b style={{ color: C.t1 }}>{ai.training_details.hardware}</b></div>}
                {ai.training_details.training_time && <div>Time: <b style={{ color: C.t1 }}>{ai.training_details.training_time}</b></div>}
                {ai.training_details.regularization?.length > 0 && <div>Regularization: <b style={{ color: C.t1 }}>{ai.training_details.regularization.join(', ')}</b></div>}
              </div>
            </div>
          )}
        </AiInsight>
      )}
    </div>
  );
}

// ── CRITIQUE TAB ──────────────────────────────────────────────────────────────
function CritiqueTab({ analysis, ai }) {
  const { reproducibility, methodology, claims, citations, readability, sections, metadata } = analysis;

  // Auto-generate critique points from structural analysis
  const strengths = [];
  const weaknesses = [];
  const assumptions = [];

  // Strengths
  if (methodology.methods.length >= 2) strengths.push('Multi-methodology approach provides robust analysis from multiple angles');
  if (methodology.isEmpirical) strengths.push('Empirical evaluation grounds theoretical contributions in real results');
  if (citations.totalRefs >= 20) strengths.push(`Comprehensive literature review (${citations.totalRefs} references) shows awareness of field`);
  if (claims.totalClaims >= 5) strengths.push(`Quantitative results (${claims.totalClaims} claims) provide measurable contributions`);
  if (methodology.datasets.length >= 2) strengths.push(`Multi-dataset evaluation (${methodology.datasets.map(d => d.name).join(', ')}) tests generalization`);
  if (methodology.metrics.length >= 3) strengths.push(`Multiple evaluation metrics (${methodology.metrics.map(m => m.name).join(', ')}) give comprehensive picture`);
  if (reproducibility.score >= 60) strengths.push('Good reproducibility signals: code, data, and hyperparameters documented');
  if (sections.some(s => s.type === 'methodology' && s.wordCount > 200)) strengths.push('Detailed methodology section enables understanding of approach');

  // Weaknesses
  if (citations.totalRefs < 15) weaknesses.push(`Limited references (${citations.totalRefs}) — may miss important related work`);
  if (!methodology.isEmpirical && methodology.methods.length < 2) weaknesses.push('Lacks empirical evaluation; claims are primarily theoretical');
  if (claims.sotaClaims > 0 && methodology.datasets.length < 2) weaknesses.push('SOTA claims made but tested on limited benchmarks');
  if (reproducibility.score < 40) weaknesses.push(`Low reproducibility score (${reproducibility.score}/100) — missing code, data, or experimental details`);
  if (methodology.metrics.length < 2) weaknesses.push('Limited evaluation metrics — single metric may not capture full picture');
  if (readability.complexityScore > 80) weaknesses.push('Very high complexity may limit accessibility to broader audience');
  if (!sections.some(s => s.type === 'discussion')) weaknesses.push('No discussion section — implications and limitations underexplored');

  // Assumptions
  if (methodology.methods.some(m => m.method === 'Supervised Learning')) assumptions.push('Assumes labeled data availability — may not generalize to low-resource settings');
  if (methodology.methods.some(m => m.method === 'Deep Learning')) assumptions.push('Assumes sufficient compute resources for training deep networks');
  if (methodology.methods.some(m => m.method === 'Transfer Learning')) assumptions.push('Assumes pre-trained models transfer well to target domain');
  if (methodology.datasets.length > 0) assumptions.push('Assumes benchmark datasets are representative of real-world distribution');
  if (claims.totalClaims > 0) assumptions.push('Implicit assumption that reported metrics are the right ones for this problem');

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      {/* Critique radar */}
      <div style={panelStyle}>
        <div style={headStyle}>🎯 Structural Critique</div>
        <div style={{ fontSize: 10, color: C.t3, marginBottom: 12, lineHeight: 1.5, borderLeft: `2px solid ${C.violet}40`, paddingLeft: 10 }}>
          This critique is generated entirely from structural analysis of the paper — section presence, citation depth,
          methodology breadth, claim density, and reproducibility signals. No subjective AI opinions are used.
        </div>
      </div>

      {/* Strengths */}
      <div style={panelStyle}>
        <div style={{ ...headStyle, color: C.green }}>✓ Identified Strengths ({strengths.length})</div>
        {strengths.length === 0 && <div style={{ fontSize: 10, color: C.t4 }}>Insufficient data to identify clear strengths.</div>}
        {strengths.map((s, i) => (
          <div key={i} style={{ padding: '6px 10px', marginBottom: 4, background: `${C.green}08`, borderRadius: 3, border: `1px solid ${C.green}20`, borderLeft: `3px solid ${C.green}` }}>
            <span style={{ fontSize: 10, color: C.t2, lineHeight: 1.6 }}>{s}</span>
          </div>
        ))}
      </div>

      {/* Weaknesses */}
      <div style={panelStyle}>
        <div style={{ ...headStyle, color: C.red }}>⚠ Potential Weaknesses ({weaknesses.length})</div>
        {weaknesses.length === 0 && <div style={{ fontSize: 10, color: C.t4 }}>No structural weaknesses detected.</div>}
        {weaknesses.map((w, i) => (
          <div key={i} style={{ padding: '6px 10px', marginBottom: 4, background: `${C.red}08`, borderRadius: 3, border: `1px solid ${C.red}20`, borderLeft: `3px solid ${C.red}` }}>
            <span style={{ fontSize: 10, color: C.t2, lineHeight: 1.6 }}>{w}</span>
          </div>
        ))}
      </div>

      {/* Assumptions */}
      <div style={panelStyle}>
        <div style={{ ...headStyle, color: C.gold }}>◈ Implicit Assumptions ({assumptions.length})</div>
        {assumptions.length === 0 && <div style={{ fontSize: 10, color: C.t4 }}>No specific assumptions detected.</div>}
        {assumptions.map((a, i) => (
          <div key={i} style={{ padding: '6px 10px', marginBottom: 4, background: `${C.gold}08`, borderRadius: 3, border: `1px solid ${C.gold}20`, borderLeft: `3px solid ${C.gold}` }}>
            <span style={{ fontSize: 10, color: C.t2, lineHeight: 1.6 }}>{a}</span>
          </div>
        ))}
      </div>

      {/* Overall assessment */}
      <div style={panelStyle}>
        <div style={headStyle}>📊 Structural Assessment Summary</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8 }}>
          <MetricCard label="Strengths" value={strengths.length} color={C.green} sub="Identified" />
          <MetricCard label="Weaknesses" value={weaknesses.length} color={C.red} sub="Potential" />
          <MetricCard label="Assumptions" value={assumptions.length} color={C.gold} sub="Implicit" />
          <MetricCard label="Reproducibility" value={`${reproducibility.score}%`} color={C.cyan} sub={reproducibility.label} />
        </div>
      </div>

      {/* ── AI-Enhanced Critique ──────────────────────────────────────────── */}
      {ai && (
        <AiInsight label="AI Expert Critique">
          {ai.overall_assessment && (
            <div style={{ fontSize: 11, color: C.t1, lineHeight: 1.7, padding: '10px 14px', background: C.surface, borderRadius: 4, marginBottom: 10 }}>
              {ai.overall_assessment}
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <span style={tagStyle(C.cyan)}>Quality: {ai.quality_score}/10</span>
                <span style={tagStyle(ai.verdict === 'accept' || ai.verdict === 'weak-accept' ? C.green : ai.verdict === 'borderline' ? C.gold : C.red)}>
                  Verdict: {ai.verdict}
                </span>
              </div>
            </div>
          )}
          {ai.strengths?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ ...headStyle, color: C.green }}>✓ Strengths (AI) ({ai.strengths.length})</div>
              {ai.strengths.map((s, i) => (
                <div key={i} style={{ padding: '6px 10px', marginBottom: 4, background: `${C.green}08`, borderRadius: 3, borderLeft: `3px solid ${C.green}` }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: C.t1 }}>{s.point}</div>
                  <div style={{ fontSize: 9, color: C.t3, marginTop: 2 }}>Evidence: {s.evidence}</div>
                </div>
              ))}
            </div>
          )}
          {ai.weaknesses?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ ...headStyle, color: C.red }}>⚠ Weaknesses (AI) ({ai.weaknesses.length})</div>
              {ai.weaknesses.map((w, i) => (
                <div key={i} style={{ padding: '6px 10px', marginBottom: 4, background: `${C.red}08`, borderRadius: 3, borderLeft: `3px solid ${w.severity === 'major' ? C.red : C.gold}` }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: C.t1 }}>{w.point}</div>
                  <div style={{ fontSize: 9, color: C.t3, marginTop: 2 }}>Evidence: {w.evidence}</div>
                  {w.suggestion && <div style={{ fontSize: 9, color: C.cyan, marginTop: 2 }}>Suggestion: {w.suggestion}</div>}
                  <span style={tagStyle(w.severity === 'major' ? C.red : C.gold)}>{w.severity}</span>
                </div>
              ))}
            </div>
          )}
          {ai.missing_elements?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={smallLabel}>Missing Elements</div>
              {ai.missing_elements.map((m, i) => (
                <div key={i} style={{ padding: '5px 10px', marginBottom: 3, background: C.surface, borderRadius: 3, borderLeft: `3px solid ${C.orange}` }}>
                  <span style={{ fontSize: 10, fontWeight: 600, color: C.t1 }}>{m.element}: </span>
                  <span style={{ fontSize: 9, color: C.t3 }}>{m.why_important}</span>
                </div>
              ))}
            </div>
          )}
          {ai.impact_assessment && (
            <div>
              <div style={smallLabel}>Impact Assessment</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 6 }}>
                {[
                  { label: 'Novelty', val: ai.impact_assessment.novelty },
                  { label: 'Significance', val: ai.impact_assessment.significance },
                  { label: 'Likely Citations', val: ai.impact_assessment.likely_citations },
                  { label: 'Practical Use', val: ai.impact_assessment.practical_applicability },
                ].filter(d => d.val).map((d, i) => (
                  <div key={i} style={{ background: C.surface, padding: 6, borderRadius: 3, border: `1px solid ${C.b0}` }}>
                    <div style={smallLabel}>{d.label}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: d.val === 'high' ? C.green : d.val === 'medium' ? C.gold : C.t3, marginTop: 2, textTransform: 'uppercase' }}>{d.val}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </AiInsight>
      )}
    </div>
  );
}

// ── REPORT TAB ────────────────────────────────────────────────────────────────
function ReportTab({ analysis, aiData, file, rawText, authUser, isAuthenticated, log }) {
  const { metadata, sections, concepts, citations, methodology, claims, readability, reproducibility } = analysis;

  const exportReport = useCallback(() => {
    const lines = [];
    lines.push('# RESEARCH PAPER ANALYSIS REPORT');
    lines.push(`Generated by Jorvex`);
    lines.push(`Date: ${new Date().toISOString().split('T')[0]}`);
    lines.push('');

    lines.push('## 1. Paper Identity');
    lines.push(`**Title:** ${metadata.title}`);
    lines.push(`**Words:** ${metadata.wordCount.toLocaleString()} | **Pages:** ~${metadata.estimatedPages} | **Reading Time:** ~${metadata.readingTimeMin} min`);
    if (metadata.abstract) lines.push(`\n**Abstract:** ${metadata.abstract.substring(0, 500)}`);
    lines.push('');

    lines.push('## 2. Structure');
    sections.forEach((s, i) => lines.push(`- **${s.title || `Section ${i + 1}`}** (${s.wordCount} words)`));
    lines.push('');

    lines.push('## 3. Key Concepts');
    concepts.concepts.slice(0, 20).forEach(c => lines.push(`- ${c.term} (freq: ${c.freq}, score: ${c.score})`));
    lines.push('');

    lines.push('## 4. Methodology');
    lines.push(`**Primary:** ${methodology.primaryMethod}`);
    methodology.methods.forEach(m => lines.push(`- ${m.method}: ${(m.confidence * 100).toFixed(0)}% confidence (${m.matchedKeywords.join(', ')})`));
    if (methodology.architectures.length) lines.push(`\n**Architectures:** ${methodology.architectures.map(a => a.name).join(', ')}`);
    if (methodology.datasets.length) lines.push(`**Datasets:** ${methodology.datasets.map(d => d.name).join(', ')}`);
    if (methodology.metrics.length) lines.push(`**Metrics:** ${methodology.metrics.map(m => m.name).join(', ')}`);
    lines.push('');

    lines.push('## 5. Claims & Results');
    lines.push(`Total: ${claims.totalClaims} | Percentage: ${claims.percentageClaims} | SOTA: ${claims.sotaClaims}`);
    claims.claims.slice(0, 10).forEach(c => lines.push(`- [${c.type}] ${c.value != null ? c.value : ''} — "${c.context}"`));
    lines.push('');

    lines.push('## 6. Citations');
    lines.push(`References: ${citations.totalRefs} | Inline: ${citations.inlineCitations} | DOIs: ${citations.dois.length}`);
    lines.push('');

    lines.push('## 7. Readability');
    lines.push(`Flesch Score: ${readability.fleschReadingEase} (${readability.difficultyLabel})`);
    lines.push(`Grade Level: ${readability.fleschKincaidGrade} (${readability.gradeLabel})`);
    lines.push(`Complexity: ${readability.complexityScore}/100`);
    lines.push('');

    lines.push('## 8. Reproducibility');
    lines.push(`Score: ${reproducibility.score}/100 — ${reproducibility.label}`);
    reproducibility.factors.forEach(f => lines.push(`- ✓ ${f.label} (+${f.pts})`));
    lines.push('');

    // AI-enhanced sections (if available)
    if (aiData) {
      lines.push('---');
      lines.push('# AI-ENHANCED ANALYSIS');
      lines.push('');
      if (aiData.extract) {
        const ex = aiData.extract;
        lines.push('## AI: Paper Identity');
        if (ex.authors?.length) lines.push(`**Authors:** ${ex.authors.join(', ')}`);
        if (ex.venue) lines.push(`**Venue:** ${ex.venue}`);
        if (ex.domain) lines.push(`**Domain:** ${ex.domain}`);
        if (ex.problem_statement) lines.push(`\n**Problem:** ${ex.problem_statement}`);
        if (ex.contributions?.length) {
          lines.push('\n**Contributions:**');
          ex.contributions.forEach(c => lines.push(`- [${c.type}/${c.novelty}] ${c.text}`));
        }
        lines.push('');
      }
      if (aiData.concepts?.core_concepts?.length) {
        lines.push('## AI: Key Concepts');
        aiData.concepts.core_concepts.forEach(c => lines.push(`- **${c.term}** ${c.is_novel ? '(NOVEL)' : ''}: ${c.definition}`));
        lines.push('');
      }
      if (aiData.results?.quantitative_results?.length) {
        lines.push('## AI: Quantitative Results');
        aiData.results.quantitative_results.forEach(r => lines.push(`- ${r.metric}: **${r.value}** on ${r.dataset}${r.is_sota ? ' 🏆 SOTA' : ''} — ${r.comparison || ''}`));
        lines.push('');
      }
      if (aiData.critique) {
        const cr = aiData.critique;
        lines.push('## AI: Expert Critique');
        if (cr.overall_assessment) lines.push(cr.overall_assessment);
        lines.push(`\nQuality: ${cr.quality_score}/10 | Verdict: ${cr.verdict}`);
        if (cr.strengths?.length) {
          lines.push('\n**Strengths:**');
          cr.strengths.forEach(s => lines.push(`- ${s.point}`));
        }
        if (cr.weaknesses?.length) {
          lines.push('\n**Weaknesses:**');
          cr.weaknesses.forEach(w => lines.push(`- [${w.severity}] ${w.point}${w.suggestion ? ` → ${w.suggestion}` : ''}`));
        }
        lines.push('');
      }
    }

    const blob = new Blob([lines.join('\n')], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `paper-analysis-${(metadata.title || 'report').substring(0, 30).replace(/[^a-zA-Z0-9]/g, '-')}.md`;
    a.click();
    URL.revokeObjectURL(url);
    log('Report exported as Markdown', 'ok');
  }, [analysis, aiData, log, metadata, sections, concepts, citations, methodology, claims, readability, reproducibility]);

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <div style={panelStyle}>
        <div style={headStyle}>📄 Analysis Report</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <button
            onClick={exportReport}
            style={{
              background: `${C.green}15`, border: `1px solid ${C.green}50`, color: C.green,
              padding: '8px 16px', borderRadius: 4, cursor: 'pointer', fontSize: 10,
              fontWeight: 700, fontFamily: FONT, letterSpacing: '.06em',
            }}
          >
            ⬇ EXPORT MARKDOWN
          </button>
        </div>
      </div>

      {/* Report summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8, marginBottom: 12 }}>
        <MetricCard label="Paper" value={metadata.title?.substring(0, 20) + '...'} color={C.cyan} sub={`${metadata.wordCount} words`} />
        <MetricCard label="Methodology" value={methodology.primaryMethod} color={C.orange} sub={`${methodology.methods.length} methods`} />
        <MetricCard label="Citations" value={citations.totalRefs} color={C.gold} sub={`${citations.inlineCitations} inline cites`} />
        <MetricCard label="Reproducibility" value={`${reproducibility.score}%`} color={reproducibility.score >= 60 ? C.green : C.red} sub={reproducibility.label} />
      </div>

      {/* Section-by-section overview */}
      <div style={panelStyle}>
        <div style={headStyle}>📑 Section Overview</div>
        {sections.filter(s => s.wordCount > 10).map((s, i) => (
          <div key={i} style={{ marginBottom: 8, padding: 10, background: C.surface, borderRadius: 3, border: `1px solid ${C.b0}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: PALETTE[i % PALETTE.length], marginBottom: 4 }}>
              {s.title || `Section ${i + 1}`}
            </div>
            <div style={{ fontSize: 9, color: C.t3, lineHeight: 1.6 }}>
              {s.text.substring(0, 200)}{s.text.length > 200 ? '...' : ''}
            </div>
            <div style={{ fontSize: 8, color: C.t4, marginTop: 4 }}>{s.wordCount} words</div>
          </div>
        ))}
      </div>

      {/* AI Report Summary */}
      {aiData && (
        <AiInsight label="AI Analysis Summary">
          {aiData.extract?.problem_statement && (
            <div style={{ fontSize: 11, color: C.t2, lineHeight: 1.7, padding: '8px 12px', background: C.surface, borderRadius: 3, marginBottom: 8 }}>
              <div style={smallLabel}>Core Problem</div>
              {aiData.extract.problem_statement}
            </div>
          )}
          {aiData.results?.main_finding && (
            <div style={{ fontSize: 11, color: C.t1, lineHeight: 1.7, padding: '8px 12px', background: `${C.green}08`, borderRadius: 3, borderLeft: `3px solid ${C.green}`, marginBottom: 8 }}>
              <div style={smallLabel}>Main Finding</div>
              {aiData.results.main_finding}
            </div>
          )}
          {aiData.critique && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <MetricCard label="AI Quality" value={`${aiData.critique.quality_score}/10`} color={C.cyan} sub="Expert rating" />
              <MetricCard label="Verdict" value={aiData.critique.verdict} color={aiData.critique.verdict === 'accept' ? C.green : C.gold} sub="Peer review" />
              <MetricCard label="Novelty" value={aiData.critique.impact_assessment?.novelty || '—'} color={C.violet} sub="Assessment" />
            </div>
          )}
        </AiInsight>
      )}
    </div>
  );
}

// ── Shared Metric Card ────────────────────────────────────────────────────────
function MetricCard({ label, value, color, sub }) {
  return (
    <div style={metricBox(color)}>
      <div style={smallLabel}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color, fontFamily: FONT, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 9, color: C.t3, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ── Sample Paper Text ─────────────────────────────────────────────────────────
const SAMPLE_ABSTRACT = `Attention Is All You Need

Ashish Vaswani, Noam Shazeer, Niki Parmar, Jakob Uszkoreit, Llion Jones, Aidan N. Gomez, Lukasz Kaiser, Illia Polosukhin

Google Brain, Google Research, University of Toronto

2017

Abstract

The dominant sequence transduction models are based on complex recurrent or convolutional neural networks that include an encoder and a decoder. The best performing models also connect the encoder and decoder through an attention mechanism. We propose a new simple network architecture, the Transformer, based solely on attention mechanisms, dispensing with recurrence and convolutions entirely. Experiments on two machine translation tasks show that these models are superior in quality while being more parallelizable and requiring significantly less time to train. Our model achieves 28.4 BLEU on the WMT 2014 English-to-German translation task, improving over the existing best results, including ensembles, by over 2 BLEU. On the WMT 2014 English-to-French translation task, our model establishes a new single-model state-of-the-art BLEU score of 41.0 after training for 3.5 days on eight GPUs, a small fraction of the training costs of the best models from the literature.

1. Introduction

Recurrent neural networks, long short-term memory and gated recurrent neural networks in particular, have been firmly established as state of the art approaches in sequence modeling and transduction problems such as language modeling and machine translation. Numerous efforts have since continued to push the boundaries of recurrent language models and encoder-decoder architectures.

Recurrent models typically factor computation along the symbol positions of the input and output sequences. Aligning the positions to steps in computation time, they generate a sequence of hidden states ht, as a function of the previous hidden state ht−1 and the input for position t. This inherently sequential nature precludes parallelization within training examples, which becomes critical at longer sequence lengths, as memory constraints limit batching across examples.

Attention mechanisms have become an integral part of compelling sequence modeling and transduction models in various tasks, allowing modeling of dependencies without regard to their distance in the input or output sequences. In all but a few cases, however, such attention mechanisms are used in conjunction with a recurrent network.

In this work we propose the Transformer, a model architecture eschewing recurrence and instead relying entirely on an attention mechanism to draw global dependencies between input and output. The Transformer allows for significantly more parallelization and can reach a new state of the art in translation quality after being trained for as little as twelve hours on eight P100 GPUs.

2. Background

The goal of reducing sequential computation also forms the foundation of the Extended Neural GPU, ByteNet and ConvS2S, all of which use convolutional neural networks as basic building block, computing hidden representations in parallel for all input and output positions. In these models, the number of operations required to relate signals from two arbitrary input or output positions grows in the distance between positions, linearly for ConvS2S and logarithmically for ByteNet. This makes it more difficult to learn dependencies between distant positions. In the Transformer this is reduced to a constant number of operations, albeit at the cost of reduced effective resolution due to averaging attention-weighted positions.

Multi-Head Attention allows the model to jointly attend to information from different representation subspaces at different positions. With a single attention head, averaging inhibits this.

3. Model Architecture

Most competitive neural sequence transduction models have an encoder-decoder structure. Here, the encoder maps an input sequence of symbol representations (x1, ..., xn) to a sequence of continuous representations z = (z1, ..., zn). Given z, the decoder then generates an output sequence (y1, ..., ym) of symbols one element at a time.

3.1 Scaled Dot-Product Attention

We call our particular attention "Scaled Dot-Product Attention". The input consists of queries and keys of dimension dk, and values of dimension dv. We compute the dot products of the query with all keys, divide each by the square root of dk, and apply a softmax function to obtain the weights on the values.

Attention(Q, K, V) = softmax(QK^T / sqrt(dk))V

The two most commonly used attention functions are additive attention and dot-product (multiplicative) attention. Dot-product attention is identical to our algorithm, except for the scaling factor of 1/sqrt(dk).

3.2 Multi-Head Attention

Instead of performing a single attention function with dmodel-dimensional keys, values and queries, we found it beneficial to linearly project the queries, keys and values h times with different, learned linear projections to dk, dk and dv dimensions, respectively.

MultiHead(Q, K, V) = Concat(head1, ..., headh)W^O

where headi = Attention(QWi^Q, KWi^K, VWi^V)

In this work we employ h = 8 parallel attention layers, or heads. For each of these we use dk = dv = dmodel/h = 64. Due to the reduced dimension of each head, the total computational cost is similar to that of single-head attention with full dimensionality.

3.3 Position-wise Feed-Forward Networks

In addition to attention sub-layers, each of the layers in our encoder and decoder contains a fully connected feed-forward network, which is applied to each position separately and identically. This consists of two linear transformations with a ReLU activation in between.

FFN(x) = max(0, xW1 + b1)W2 + b2

The dimensionality of input and output is dmodel = 512, and the inner-layer has dimensionality dff = 2048.

3.4 Positional Encoding

Since our model contains no recurrence and no convolution, in order for the model to make use of the order of the sequence, we must inject some information about the relative or absolute position of the tokens in the sequence. To this end, we add "positional encodings" to the input embeddings at the bottoms of the encoder and decoder stacks.

PE(pos, 2i) = sin(pos / 10000^(2i/dmodel))
PE(pos, 2i+1) = cos(pos / 10000^(2i/dmodel))

4. Why Self-Attention

In this section we compare various aspects of self-attention layers to the recurrent and convolutional layers commonly used for mapping one variable-length sequence of symbol representations to another sequence of equal length.

One is the total computational complexity per layer. Another is the amount of computation that can be parallelized, as measured by the minimum number of sequential operations required.

A self-attention layer connects all positions with a constant number of sequentially executed operations, whereas a recurrent layer requires O(n) sequential operations.

5. Training

5.1 Training Data and Batching

We trained on the standard WMT 2014 English-German dataset consisting of about 4.5 million sentence pairs. Sentences were encoded using byte-pair encoding, which has a shared source-target vocabulary of about 37000 tokens. For English-French, we used the significantly larger WMT 2014 English-French dataset consisting of 36M sentences and split tokens into a 32000 word-piece vocabulary.

5.2 Hardware and Schedule

We trained our models on one machine with 8 NVIDIA P100 GPUs. For our base models, each training step took about 0.4 seconds. We trained the base models for a total of 100,000 steps or 12 hours. For our big models, step time was 1.0 seconds. The big models were trained for 300,000 steps (3.5 days).

5.3 Optimizer

We used the Adam optimizer with β1 = 0.9, β2 = 0.98 and ε = 10^−9. We varied the learning rate over the course of training, according to the formula:

lrate = d^(−0.5)_model · min(step_num^(−0.5), step_num · warmup_steps^(−1.5))

This corresponds to increasing the learning rate linearly for the first warmup_steps training steps, and decreasing it thereafter proportionally to the inverse square root of the step number. We used warmup_steps = 4000.

5.4 Regularization

We employ three types of regularization during training:

Residual Dropout: We apply dropout to the output of each sub-layer, before it is added to the sub-layer input and normalized. In addition, we apply dropout to the sums of the embeddings and the positional encodings in both the encoder and decoder stacks. For the base model, we use a rate of Pdrop = 0.1.

Label Smoothing: During training, we employed label smoothing of value εls = 0.1. This hurts perplexity, as the model learns to be more unsure, but improves accuracy and BLEU score.

6. Results

6.1 Machine Translation

On the WMT 2014 English-to-German translation task, the big transformer model outperforms the best previously reported models including ensembles by more than 2.0 BLEU, establishing a new state-of-the-art BLEU score of 28.4. The configuration of this model is listed in the bottom line of Table 3. Training took 3.5 days on 8 P100 GPUs. Even our base model surpasses all previously published models and ensembles, at a fraction of the training cost of any of the competitive models.

On the WMT 2014 English-to-French translation task, our big model achieves a BLEU score of 41.0, outperforming all of the previously published single models, at less than 1/4 the training cost of the previous state-of-the-art model. The Transformer (big) model trained for English-to-French used dropout rate Pdrop = 0.1, instead of 0.3.

6.2 Model Variations

To evaluate the importance of different components of the Transformer, we varied our base model in different ways, measuring the change in performance on English-to-German translation on the development set, newstest2013. We used beam search as described in the previous section, but no checkpoint averaging. We present these results in Table 3.

In Table 3 rows (A), we vary the number of attention heads and the attention key and value dimensions, keeping the amount of computation constant. While single-head attention is 0.9 BLEU worse than the best setting, quality also drops off with too many heads.

7. Conclusion

In this work, we presented the Transformer, the first sequence transduction model based entirely on attention, replacing the recurrent layers most commonly used in encoder-decoder architectures with multi-headed self-attention.

For translation tasks, the Transformer can be trained significantly faster than architectures based on recurrent or convolutional layers. On both WMT 2014 English-to-German and WMT 2014 English-to-French translation tasks, we achieve a new state of the art. In the former task our best model outperforms even all previously reported ensembles.

We plan to extend the Transformer to problems involving input and output modalities other than text and to investigate local, restricted attention mechanisms to efficiently handle large inputs and outputs such as images, audio and video. Making generation less sequential is another research goal of ours.

References

[1] Jimmy Lei Ba, Jamie Ryan Kiros, and Geoffrey E Hinton. Layer normalization. (2016)
[2] Dzmitry Bahdanau, Kyunghyun Cho, and Yoshua Bengio. Neural machine translation by jointly learning to align and translate. (2014)
[3] Denny Britz, Anna Goldie, Minh-Thang Luong, and Quoc V Le. Massive exploration of neural machine translation architectures. (2017)
[4] Jonas Gehring, Michael Auli, David Grangier, Denis Yarats, and Yann N Dauphin. Convolutional sequence to sequence learning. (2017)
[5] Alex Graves. Generating sequences with recurrent neural networks. (2013)
[6] Kaiming He, Xiangyu Zhang, Shaoqing Ren, and Jian Sun. Deep residual learning for image recognition. (2016)
[7] Sepp Hochreiter and Jurgen Schmidhuber. Long short-term memory. Neural computation, 9(8):1735-1780. (1997)
[8] Diederik Kingma and Jimmy Ba. Adam: A method for stochastic optimization. (2014)
[9] Minh-Thang Luong, Hieu Pham, and Christopher D Manning. Effective approaches to attention-based neural machine translation. (2015)
[10] Ankur Parikh, Oscar Tackstrom, Dipanjan Das, and Jakob Uszkoreit. A decomposable attention model. (2016)
[11] Ofir Press and Lior Wolf. Using the output embedding to improve language models. (2016)
[12] Rico Sennrich, Barry Haddow, and Alexandra Birch. Neural machine translation of rare words with subword units. (2015)
[13] Ilya Sutskever, Oriol Vinyals, and Quoc V Le. Sequence to sequence learning with neural networks. (2014)
[14] Yonghui Wu, Mike Schuster, Zhifeng Chen, et al. Google's neural machine translation system: Bridging the gap between human and machine translation. (2016)
[15] Matthew D Zeiler. ADADELTA: An adaptive learning rate method. (2012)`;
